import { BACKEND_KEYS } from '../../common/consts/app-keys.const';
import { HttpFactoryService, HttpService } from '../../common/services';

export interface ILoginEvent {
  id: number;
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  success: boolean;
}

export interface IAdminUser {
  id: number;
  email: string;
  createdAt: string;
  isActivated: boolean;
  role: string;
  adminNotes: string | null;
  lastLoginAt: string | null;
  failedAttempts: number;
  isLocked: boolean;
  subscriptionStatus: 'active' | 'trialing' | 'past_due' | 'canceled' | null;
  subscriptionTier: 'monthly' | 'yearly' | 'trial' | null;
  subscriptionEndsAt: string | null;
  hasActiveSubscription: boolean;
  isProGifted: boolean;
  hasFreeTrialAbility: boolean;
  subscriptionCancelledAt: string | null;
}

class AdminService {
  httpService: HttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createHttpService();
  }

  async getUsers(): Promise<IAdminUser[]> {
    const response = await this.httpService.get<IAdminUser[]>(BACKEND_KEYS.ADMIN_USERS);
    return (response as IAdminUser[]) ?? [];
  }

  async resendActivation(userId: number): Promise<void> {
    await this.httpService.post(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/resend-activation`, {});
  }

  async forceActivate(userId: number): Promise<void> {
    await this.httpService.post(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/force-activate`, {});
  }

  async getLoginHistory(userId: number): Promise<ILoginEvent[]> {
    const response = await this.httpService.get<ILoginEvent[]>(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/login-history`);
    return (response as ILoginEvent[]) ?? [];
  }

  async updateNotes(userId: number, notes: string): Promise<void> {
    await this.httpService.put(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/notes`, { notes });
  }

  async toggleProGifted(userId: number): Promise<void> {
    await this.httpService.put(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/toggle-pro-gifted`, {});
  }

  async resetFreeTrial(userId: number): Promise<void> {
    await this.httpService.put(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/reset-trial`, {});
  }

  async deleteUser(userId: number, emailUser: boolean = true): Promise<void> {
    await this.httpService.delete(`${BACKEND_KEYS.ADMIN_USERS}/${userId}?emailUser=${emailUser}`);
  }

  async toggleEnterprise(userId: number): Promise<void> {
    await this.httpService.put(`${BACKEND_KEYS.ADMIN_USERS}/${userId}/toggle-enterprise`, {});
  }

  async getAllCompanies(): Promise<any[]> {
    const response = await this.httpService.get<any[]>('company/all');
    return (response as any[]) ?? [];
  }

  async createCompany(name: string, slug: string, ownerId: number): Promise<any> {
    return this.httpService.post('company', { name, slug, ownerId });
  }

  async sendProInvite(email: string): Promise<void> {
    await this.httpService.post(BACKEND_KEYS.INVITE_PRO, { email });
  }
}

const adminService = new AdminService(new HttpFactoryService());

export { adminService };
